import { CacheService, CustomerService, NumericFormatterValueConverter, property, widget } from '@dynamik/common';
import { combineLatestWith, debounceTime, delayWhen, ReplaySubject, switchMap, takeUntil, tap } from '../../../rxjs-local';
import { ColumnState, GetContextMenuItemsParams, GridApi, GridOptions, ValueFormatterParams } from 'ag-grid-community';
import { BindingEngine } from 'aurelia-binding';
import { WidgetBase } from '@dynamik/common';
import { getExcelStyles } from '../../../resources/agGrid/excel-styles';
import { customElement } from 'aurelia-templating';
import { ActionType, GroupedTransactionsViewModel, TransactionsClient } from '@dynamik/common/clients/transaction-clients';
import { dateFilterParams } from '../../../agElements/filters/dateFilter';
import { ReportPageLayoutDeviceType } from '@dynamik/common/clients/reporting-clients';
import { ThemeService } from '../../../services/theme-service';

@customElement('processed-vouchers-widget')
@widget('Processed vouchers', false, true, 'Description', [ReportPageLayoutDeviceType.Desktop])
export class ProcessedVouchersWidget extends WidgetBase {
  constructor(element: Element, customerService: CustomerService, private transactionClient: TransactionsClient, public themeService: ThemeService,
    bindingEngine: BindingEngine, private numericFormatter: NumericFormatterValueConverter, private cacheService: CacheService) {
    super(element, bindingEngine, customerService);
  }

  gridApi: GridApi;
  suppressSaveColumnState: boolean;
  gridOptions: GridOptions<GroupedTransactionsViewModel>;
  gridReady$ = new ReplaySubject<void>(1);
  quantityFormatter = (params: ValueFormatterParams) => this.numericFormatter.toView(params.value as number, this.quantityPrecision);
  moneyFormatter = (params: ValueFormatterParams) => this.numericFormatter.toView(params.value as number, this.precision);
  costPriceEaFormatter = (params: ValueFormatterParams) => params.data?.quantity
    ? this.numericFormatter.toView(params.data.costPrice / params.data.quantity, this.precision)
    : '';

  @property(null)
  columnStates?: ColumnState[];

  @property(null)
  filterModel?: ReturnType<GridApi['getFilterModel']>;

  @property('Data')
  groupsClosed: boolean;

  @property('Data')
  hidePaging: boolean;

  @property('Data')
  quantityPrecision?: number = 0;

  @property('Data')
  precision: number = 2;

  @property('Table')
  fontSize?: number = 8;

  @property('Table')
  rowHeight?: number = 20;

  override subscribeToFilter() {
    this.customerService.customerAndDates$
      .pipe(
        delayWhen(() => this.gridReady$),
        combineLatestWith(this.refresh$),
        debounceTime(500),
        tap(() => {
          this.error = undefined;
          this.gridApi.setGridOption('loading', true);
          this.notifyLoading();
        }),
        switchMap(async ([filter]) => this.cacheService.getOrSet(`processed-vouchers-widget-${filter.customer.companyId}-${filter.dates.fromStr}-${filter.dates.toStr}`,
          () => this.transactionClient.grouped(filter.customer.companyId, undefined, false, undefined, undefined, filter.dates.fromStr, filter.dates.toStr, undefined), 60000, false)
          .catch(e => e as Error)),
        takeUntil(this.destroy$)
      )
      .subscribe(x => {
        this.gridApi.setGridOption('loading', false);
        if (x instanceof Error) {
          this.error = x;
        } else {
          x.forEach(y => y.amount = Math.abs(y.amount));
          this.gridApi.setGridOption('rowData', x);
          this.notifyLoaded();
        }
      });
  }

  override activate(config: unknown) {
    super.activate(config)
    this.gridOptions = {
      defaultColDef: {
        filter: 'agTextColumnFilter',
        sortable: true,
        resizable: true,
      },
      getContextMenuItems: params => this.getContextMenuItems(params),
      rowHeight: this.rowHeight,
      excelStyles: getExcelStyles(9),
      rowModelType: 'clientSide',
      rowSelection: { mode: 'singleRow', checkboxes: false },
      paginationAutoPageSize: true,
      pagination: true,
      paginateChildRows: true,
      groupDefaultExpanded: this.groupsClosed ? 0 : -1,
      groupTotalRow: 'bottom',
      suppressAggFuncInHeader: true,
      groupHideOpenParents: false,
      animateRows: false,
      debug: false,
      scrollbarWidth: 11,
      onColumnMoved: () => this.saveColumnState(),
      onColumnVisible: () => this.saveColumnState(),
      onColumnResized: () => this.saveColumnState(),
      onFilterChanged: () => this.saveColumnState(),
      onSortChanged: () => this.saveColumnState(),
      onDisplayedColumnsChanged: params => {
        if (params.source !== 'gridInitializing') {
          this.saveColumnState();
        }
      },
      onGridReady: params => {
        this.gridApi = params.api;
        this.suppressSaveColumnState = true;
        setTimeout(() => this.suppressSaveColumnState = false, 2000);
        if (this.columnStates?.length) {
          this.gridApi.applyColumnState({ state: this.columnStates, applyOrder: true });
        }
        if (this.filterModel?.length) {
          this.gridApi.setFilterModel(this.filterModel);
        }
        this.gridReady$.next();
      }
    };
  }

  getPageCount(): number {
    return this.gridApi.paginationGetTotalPages();
  }

  async setPage(page: number): Promise<void> {
    if (page === this.gridApi.paginationGetCurrentPage()) {
      return;
    }
    this.gridApi.paginationGoToPage(page);
    // give the grid time to render
    await Promise.delay(100);
  }

  override configChanged(key: keyof ProcessedVouchersWidget) {
    switch (key) {
      case 'rowHeight':
        this.rowHeightChanged();
        break;
      case 'quantityPrecision':
      case 'precision':
        this.refresh$.next();
        break;
      default:
        break;
    }
  }

  saveColumnState() {
    if (this.suppressSaveColumnState) {
      return;
    }
    this.columnStates = this.gridApi.getColumnState();
    this.filterModel = this.gridApi.getFilterModel();
    this.element.dispatchEvent(new CustomEvent('widget-change', { bubbles: true }));
  }

  rowHeightChanged() {
    this.gridOptions.rowHeight = this.rowHeight;
    this.gridApi.resetRowHeights();
  }

  getContextMenuItems(params: GetContextMenuItemsParams<GroupedTransactionsViewModel>) {
    return [
      'autoSizeAll',
      'resetColumns',
      { name: 'Fit All Columns', action: () => this.gridApi.sizeColumnsToFit() },
      'separator',
      {
        name: 'Export to Csv',
        action: async () => params.api.exportDataAsCsv({ columnSeparator: ';' }),
      },
      {
        name: 'Export to Excel',
        action: async () => params.api.exportDataAsExcel({

          skipPinnedTop: false,
          skipPinnedBottom: false,
          allColumns: false,
          onlySelected: false,
          fileName: 'ProcessedVouchers',
          sheetName: 'ProcessedVouchers'
        }),
      }
    ];
  }

  actionTypeValueFormatter = (params: ValueFormatterParams<GroupedTransactionsViewModel>) => {
    if (!params.data && !params.value) {
      return '';
    }
    return ActionType[params.value] ?? '';
  };

  getDateFilterParams() {
    return dateFilterParams;
  }

}
